import { type ComponentProps, useEffect, useReducer, useRef } from "react";
import { DEFAULT_AVATAR_PATH } from "~/constants/app";
import { cn } from "~/lib/cn";

const MAX_RETRIES = 2;
const RETRY_DELAY = 500;

type ImageState = {
  prevSrc: string | undefined;
  retryCount: number;
  failed: boolean;
  isLoading: boolean;
};

type ImageAction =
  | { type: "srcChanged"; src: string }
  | { type: "retry"; count: number }
  | { type: "failed" }
  | { type: "loaded" };

const imageReducer = (state: ImageState, action: ImageAction): ImageState => {
  switch (action.type) {
    case "srcChanged":
      return {
        prevSrc: action.src,
        retryCount: 0,
        failed: false,
        isLoading: true,
      };
    case "retry":
      return { ...state, retryCount: action.count };
    case "failed":
      return { ...state, failed: true };
    case "loaded":
      return { ...state, isLoading: false };
  }
};

type CachedImageProps = Omit<ComponentProps<"img">, "onError"> & {
  fallbackClassName?: string;
  fallbackSrc?: string;
};

export const CachedImage = ({
  src,
  fallbackClassName,
  fallbackSrc = DEFAULT_AVATAR_PATH,
  className,
  alt,
  ...props
}: CachedImageProps) => {
  const [{ prevSrc, retryCount, failed, isLoading }, dispatch] = useReducer(
    imageReducer,
    { prevSrc: src, retryCount: 0, failed: false, isLoading: !!src },
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  // src 変更時にインラインでリセット（useEffect によるprop同期を避ける）
  if (src !== prevSrc && src) {
    dispatch({ type: "srcChanged", src });
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!src) {
    return <div className={fallbackClassName ?? className} />;
  }

  if (failed) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={cn("object-cover", fallbackClassName ?? className)}
      />
    );
  }

  // リトライ時はキャッシュバスティングパラメータを付与して新規 HTTP リクエストを強制する
  const imgSrc = retryCount > 0 ? `${src}?_retry=${retryCount}` : src;

  const handleError = () => {
    if (retryCount < MAX_RETRIES) {
      const next = retryCount + 1;
      console.warn(
        `[CachedImage] 取得失敗 → リトライ ${next}/${MAX_RETRIES} 回目 | ${src}`,
      );
      timerRef.current = setTimeout(() => {
        dispatch({ type: "retry", count: next });
      }, RETRY_DELAY);
    } else {
      console.error(
        `[CachedImage] 全リトライ失敗（${MAX_RETRIES} 回）→ フォールバック表示 | ${src}`,
      );
      dispatch({ type: "failed" });
    }
  };

  const handleLoad = () => {
    if (retryCount > 0) {
      console.info(
        `[CachedImage] リトライ ${retryCount}/${MAX_RETRIES} 回目で成功 | ${src}`,
      );
    }
    dispatch({ type: "loaded" });
  };

  return (
    <div className={cn("relative overflow-hidden", className)}>
      <img
        key={imgSrc}
        src={imgSrc}
        alt={alt}
        className={cn(
          "absolute inset-0 transition-opacity duration-300",
          className,
          isLoading ? "opacity-0" : "opacity-100",
        )}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
      {isLoading && (
        <div role="status" className="absolute inset-0 animate-shimmer" />
      )}
    </div>
  );
};
